import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Inbox, Image as ImageIcon, FileText, Search, CheckCircle2, Link2, ListMusic, Music, Upload, MonitorPlay, Clock, Trash2 } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@meno/ui/components/ui/dialog'
import { Button } from '@meno/ui/components/ui/button'
import { Input } from '@meno/ui/components/ui/input'
import { Badge } from '@meno/ui/components/ui/badge'
import { Skeleton } from '@meno/ui/components/ui/skeleton'
import { TabButton } from '@meno/ui/components/shared/TabButton'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@meno/ui/components/ui/select'
import { getMedia, getAudios, getPlaylists, BACKEND_URL } from '@meno/ui/lib/api'
import { loadLinks, type LinkItem } from '@/lib/interactiveLinks'
import { playlistThumbUrl } from '@/lib/playlistThumb'
import PdfThumbnail from '@/components/PdfThumbnail'
import { useUpload } from '@/contexts/UploadContext'

// Asset shape returned by /media — same fields the playlist edit dialog
// already uses. Re-typed inline so the modal stays self-contained.
type AssetItem = {
  id: number
  original_name: string
  file_type: string
  url: string
  duration?: number
  file_size?: number
  created_at?: string
}

// Picked items returned to the caller. `kind` lets the caller route the
// item to media_ids / link_ids / playlist_ids on the backend.
export type PickedItem =
  | ({ kind: 'media' } & AssetItem)
  | ({ kind: 'audio' } & AssetItem)
  | { kind: 'link';      id: number; name: string; interactive_link: string; duration?: number }
  | { kind: 'playlist';  id: number; name: string; item_count: number }

type PlaylistItem = {
  id: number
  name: string
  // Need file_type + url on each media row so playlistThumbUrl can pick the
  // first renderable preview (image url, or a cached video frame). The picker
  // is happy with a lighter shape, but the thumbnail helper needs both.
  media?: Array<{ id: number; file_type?: string; url?: string }>
  links?: Array<{ id: number }>
}

export type PickerTabKey = 'all' | 'media' | 'audio' | 'documents' | 'links' | 'playlists' | 'current' | 'selected'

// ── Custom-groups single-select mode ────────────────────────────────────────
// A caller (Mix ingredients) can hand the picker a ready-made, already-
// resolved set of tiles instead of letting it self-fetch media/links/
// playlists — this is how "pick the one playlist-or-layout" / "pick the one
// stop screen" flows reuse the SAME picker chrome (search, grid, Dialog vs.
// embedded) as the multi-select media picker, instead of a second bespoke
// modal component. `PickedItem` doesn't fit these (they're not media/link/
// playlist rows), so custom mode talks in opaque `${group.key}:${item.id}`
// strings via onCustomSelect instead.
export type PickerCardItem = {
  id: number
  label: string
  /** Resolved image URL — image/video-backed content shows this; audio/stop
   *  screens without a renderable frame fall back to the icon tile. */
  thumb?: string | null
  icon: typeof CheckCircle2
  colorClass: string
}
export type PickerGroup = { key: string; label: string; items: PickerCardItem[] }

type Props = {
  open: boolean
  /** Media IDs already assigned — pre-selected and disabled. */
  excludeIds?: number[]
  /** Link IDs already assigned — pre-selected and disabled. */
  excludeLinkIds?: number[]
  /** Playlist IDs already assigned — pre-selected and disabled. */
  excludePlaylistIds?: number[]
  /** Which tabs to expose. Defaults to the original four (no playlists tab)
   *  so PlaylistDetailsPage doesn't accidentally let an admin nest a
   *  playlist inside another playlist. ScreenDetailsPage opts into the
   *  full five-tab set including 'playlists'. */
  tabs?: PickerTabKey[]
  /** Audio-only mode — the picker loads the org's audio library (instead of
   *  media + links + playlists), shows a single Audio tab, accepts audio
   *  uploads, and returns picks as `{ kind: 'audio' }`. Lets the Audio tab of
   *  the playlist/layout editors reuse this modal so "Add Audio" mirrors
   *  "Add Content" but scoped to audio. `excludeIds` then refers to the
   *  audio IDs already attached. */
  audioOnly?: boolean
  /** When true, items in the exclude lists are pre-selected and *togglable*
   *  (rather than disabled), and `onConfirm` returns the full desired set so
   *  the caller can both add new items and remove deselected ones. Used by
   *  the playlist editor; ScreenDetailsPage opts in to let operators
   *  deselect already-assigned content directly from the picker. */
  removable?: boolean
  /** Label for the primary confirm button. Default: "Add to playlist". */
  confirmLabel?: string
  /** Empty-state hint when no current content is assigned (Currently Showing tab). */
  currentEmptyLabel?: string
  /** 'modal' (default) renders the usual Dialog + footer. 'embedded' renders
   *  just the tab strip / search / grid as a plain block — no Dialog chrome,
   *  no confirm footer — so a caller (PlaylistDialog) can host the picker
   *  directly inside its own layout instead of opening it as a nested modal.
   *  Embedded mode always behaves like `removable`: the exclude lists seed
   *  the working selection and stay togglable. */
  mode?: 'modal' | 'embedded'
  /** Embedded mode only — fires with the full working selection on every
   *  toggle / duration edit, so the host can mirror it into its own state. */
  onSelectionChange?: (selected: PickedItem[]) => void
  /** Caller-supplied groups — switches the picker into single-select custom
   *  mode (see the type's doc comment above). When set, `tabs`/`audioOnly`/
   *  `removable`/exclude lists/onConfirm/onSelectionChange are all ignored;
   *  use `customSelected` + `onCustomSelect` instead. */
  customGroups?: PickerGroup[]
  /** `${group.key}:${item.id}` of the current pick, or null. */
  customSelected?: string | null
  onCustomSelect?: (value: string | null) => void
  /** Custom mode only — shown in a DialogHeader above the search bar (native
   *  mode has no header; its tab strip identifies the picker instead). */
  title?: string
  /** Override the search box's placeholder (defaults to "Search"). */
  searchPlaceholder?: string
  onClose: () => void
  onConfirm: (selected: PickedItem[]) => void
}

const DEFAULT_TABS: PickerTabKey[] = ['all', 'media', 'documents', 'links']

// Fallback per-item display time (seconds) when an asset carries no duration
// of its own — matches the player's default rotation dwell.
const DEFAULT_DURATION = 10

// PlaylistContentPicker — modal that mirrors the Asset page's tab strip +
// search + filter pattern, scoped to the asset types that can sit in a
// playlist (images / videos / GIFs / PDFs / interactive links). Multi-select
// across all tabs with a single confirm. The caller decides what to do
// with the picked items (typically append to playlist via PATCH).
export default function PlaylistContentPicker({
  open, excludeIds = [], excludeLinkIds = [], excludePlaylistIds = [],
  tabs = DEFAULT_TABS, audioOnly = false, removable = false,
  confirmLabel, currentEmptyLabel,
  mode = 'modal', onSelectionChange,
  customGroups, customSelected = null, onCustomSelect, title, searchPlaceholder,
  onClose, onConfirm,
}: Props) {
  const { t } = useTranslation()
  const embedded = mode === 'embedded'
  const isCustom = customGroups != null
  // Embedded mode always behaves like `removable` — pre-seeded and togglable.
  const effectiveRemovable = removable || embedded
  const [tab, setTab] = useState<PickerTabKey>('all')
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<'all' | 'image' | 'video' | 'gif'>('all')
  const [items, setItems] = useState<AssetItem[]>([])
  const [links, setLinks] = useState<LinkItem[]>([])
  const [playlists, setPlaylists] = useState<PlaylistItem[]>([])
  // Custom mode has nothing to fetch — its data arrives pre-loaded as a prop.
  const [loading, setLoading] = useState(!isCustom)
  const { openUpload } = useUpload()
  const [selectedMedia, setSelectedMedia] = useState<Set<number>>(new Set())
  const [selectedLinks, setSelectedLinks] = useState<Set<number>>(new Set())
  const [selectedPlaylists, setSelectedPlaylists] = useState<Set<number>>(new Set())
  // ONE ordered record of the whole selection, across all three kinds:
  // `["media:12", "link:3", "media:8"]` in the exact sequence the operator
  // clicked. The three Sets above answer "is this selected?"; this answers
  // "in what order?", which the Sets cannot — each preserves insertion order
  // only WITHIN its own kind, so a pick of link → image → link came out as
  // image, link, link once the three were concatenated. That reordering then
  // rode all the way through to the published playlist, so what played on the
  // wall was not the sequence the operator built.
  const [pickOrder, setPickOrder] = useState<string[]>([])
  const pickKey = (kind: 'media' | 'link' | 'playlist', id: number) => `${kind}:${id}`
  const notePick = (kind: 'media' | 'link' | 'playlist', id: number, selected: boolean) =>
    setPickOrder(prev => {
      const key = pickKey(kind, id)
      const without = prev.filter(k => k !== key)
      return selected ? [...without, key] : without
    })
  // Per-item display time (seconds), keyed by media id. Seeded from each
  // asset's own duration on first select; editable inline on the tile.
  const [durations, setDurations] = useState<Record<number, number>>({})
  // Same idea for links — links have no natural duration, so every pick
  // seeds the default and the tile exposes the same clock/input control.
  const [linkDurations, setLinkDurations] = useState<Record<number, number>>({})

  const includePlaylists = tabs.includes('playlists')

  // Load assets + links (+ playlists if exposed) when the modal opens.
  // In `effectiveRemovable` mode (explicit `removable`, or always-on for
  // `embedded`) the already-assigned items are pre-selected so the user can
  // toggle them off to remove; otherwise selection starts empty.
  useEffect(() => {
    if (!open || isCustom) return
    setLoading(true)
    setSelectedMedia(effectiveRemovable ? new Set(excludeIds) : new Set())
    setSelectedLinks(effectiveRemovable ? new Set(excludeLinkIds) : new Set())
    setSelectedPlaylists(effectiveRemovable ? new Set(excludePlaylistIds) : new Set())
    // Seed the order from the already-assigned items so removable mode opens
    // showing the screen's CURRENT order, not a re-derived one.
    setPickOrder(effectiveRemovable
      ? [
          ...excludeIds.map(id => `media:${id}`),
          ...excludeLinkIds.map(id => `link:${id}`),
          ...excludePlaylistIds.map(id => `playlist:${id}`),
        ]
      : [])
    setDurations({})
    setLinkDurations({})
    setSearch('')
    setTab(audioOnly ? 'audio' : 'all')
    setTypeFilter('all')
    const reqs: Promise<unknown>[] = audioOnly
      ? [getAudios().then(r => setItems(r.data)).catch(() => {})]
      : [
          getMedia(false, 'signage').then(r => setItems(r.data)).catch(() => {}),
          loadLinks().then(setLinks).catch(() => {}),
        ]
    if (!audioOnly && includePlaylists) {
      reqs.push(getPlaylists().then(r => setPlaylists(r.data)).catch(() => {}))
    }
    Promise.all(reqs).finally(() => setLoading(false))
    // exclude* lists are stable per-open; intentionally not in deps to avoid
    // clobbering the user's in-modal selection on every parent re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, includePlaylists, audioOnly, effectiveRemovable, isCustom])

  // Quick-upload runs through the app-wide universal upload modal — the same
  // Assets-page experience (per-type crop / PDF / audio, batch-aware). Each
  // uploaded asset is appended to the grid + auto-selected as it lands; once
  // the batch drains we hop off any tab that would hide the new items.
  const openPickerUpload = () => {
    let sawPdf = false
    openUpload({
      accept: audioOnly ? 'audio/*' : undefined,
      allowAudio: audioOnly,
      onUploaded: (asset) => {
        const item = (asset.kind === 'audio' ? asset.audio : asset.file) as AssetItem
        // Media pickers ignore audio and vice-versa — only take the matching kind.
        if (audioOnly !== (asset.kind === 'audio')) return
        setItems(prev => [item, ...prev])
        setSelectedMedia(prev => { const next = new Set(prev); next.add(item.id); return next })
        // Auto-selected on arrival, so it joins the ordered selection too —
        // otherwise a just-uploaded asset would carry no order badge and get
        // dropped from buildPicked's ordered walk.
        notePick('media', item.id, true)
        if (item.file_type === 'application/pdf' || /\.pdf$/i.test(item.original_name)) sawPdf = true
      },
      onComplete: () => {
        if (audioOnly) return
        if (tab === 'links' || tab === 'playlists') setTab('all')
        else if (tab === 'media' && sawPdf) setTab(tabs.includes('documents') ? 'documents' : 'all')
      },
    })
  }

  // Custom mode — filter the caller-supplied groups by the same search box,
  // dropping any group left empty (mirrors the old MixContentPicker).
  const filteredCustomGroups = useMemo(() => {
    if (!customGroups) return []
    const q = search.trim().toLowerCase()
    return customGroups
      .map(g => ({ ...g, items: q ? g.items.filter(i => i.label.toLowerCase().includes(q)) : g.items }))
      .filter(g => g.items.length > 0)
  }, [customGroups, search])
  const pickCustom = (value: string) => {
    const next = customSelected === value ? null : value
    onCustomSelect?.(next)
    // Single-select: picking a tile is the whole interaction, same as the
    // old MixContentPicker — no separate Confirm step in modal mode.
    if (!embedded) onClose()
  }

  const excluded          = useMemo(() => new Set(excludeIds),          [excludeIds])
  const excludedLinks     = useMemo(() => new Set(excludeLinkIds),      [excludeLinkIds])
  const excludedPlaylists = useMemo(() => new Set(excludePlaylistIds),  [excludePlaylistIds])

  const filteredMedia = useMemo(() => {
    if (tab === 'links' || tab === 'playlists') return []
    const q = search.trim().toLowerCase()
    return items.filter(a => {
      const isPdf = a.file_type === 'application/pdf' || /\.pdf$/i.test(a.original_name)
      // Selected tab: only the items in the working selection, regardless of
      // type — a single place to review and remove picks before confirming.
      if (tab === 'selected') return selectedMedia.has(a.id) && (!q || a.original_name.toLowerCase().includes(q))
      // Currently-Showing tab: only items already assigned to the caller
      // (matches against the exclude lists since those represent the
      // currently-attached set).
      if (tab === 'current' && !excluded.has(a.id)) return false
      if (tab === 'media' && isPdf) return false
      if (tab === 'media' && !(a.file_type.startsWith('image') || a.file_type.startsWith('video'))) return false
      if (tab === 'documents' && !isPdf) return false
      if (tab !== 'documents' && tab !== 'current' && typeFilter !== 'all') {
        if (typeFilter === 'image' && !(a.file_type.startsWith('image') && a.file_type !== 'image/gif')) return false
        if (typeFilter === 'gif'   && a.file_type !== 'image/gif') return false
        if (typeFilter === 'video' && !a.file_type.startsWith('video')) return false
      }
      if (q && !a.original_name.toLowerCase().includes(q)) return false
      return true
    })
  }, [items, search, typeFilter, tab, excluded, selectedMedia])

  const filteredLinks = useMemo(() => {
    if (tab !== 'all' && tab !== 'links' && tab !== 'current' && tab !== 'selected') return []
    const q = search.trim().toLowerCase()
    return links.filter(l => {
      if (tab === 'selected' && !selectedLinks.has(l.id)) return false
      if (tab === 'current' && !excludedLinks.has(l.id)) return false
      if (!q) return true
      return l.name.toLowerCase().includes(q) || l.interactive_link.toLowerCase().includes(q)
    })
  }, [links, search, tab, excludedLinks, selectedLinks])

  const filteredPlaylists = useMemo(() => {
    if (tab === 'current') {
      return playlists.filter(p => excludedPlaylists.has(p.id))
    }
    if (tab === 'selected') {
      const q = search.trim().toLowerCase()
      return playlists.filter(p => selectedPlaylists.has(p.id) && (!q || p.name.toLowerCase().includes(q)))
    }
    if (!includePlaylists) return []
    if (tab !== 'all' && tab !== 'playlists') return []
    const q = search.trim().toLowerCase()
    return playlists.filter(p => !q || p.name.toLowerCase().includes(q))
  }, [playlists, search, tab, includePlaylists, excludedPlaylists, selectedPlaylists])

  const toggleMedia = (id: number) => {
    if (!effectiveRemovable && excluded.has(id)) return
    setSelectedMedia(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      notePick('media', id, next.has(id))
      return next
    })
    // Seed this item's time from its own duration the first time it's picked.
    setDurations(prev => {
      if (prev[id] != null) return prev
      const asset = items.find(a => a.id === id)
      return { ...prev, [id]: Math.max(1, Math.round(asset?.duration ?? DEFAULT_DURATION)) }
    })
  }
  const setDuration = (id: number, seconds: number) =>
    setDurations(prev => ({ ...prev, [id]: Math.max(1, Math.min(86400, Math.round(seconds) || 1)) }))

  // 1-based display order badges, numbered across the ENTIRE selection rather
  // than per-kind — the badge has to promise the same sequence that will
  // actually be published, and media/links/playlists all share one rotation.
  const orderBadges = useMemo(() => {
    const m = new Map<string, number>()
    pickOrder.forEach((key, i) => m.set(key, i + 1))
    return m
  }, [pickOrder])
  const mediaOrder = useMemo(() => {
    const m = new Map<number, number>()
    ;[...selectedMedia].forEach(id => {
      const n = orderBadges.get(`media:${id}`)
      if (n != null) m.set(id, n)
    })
    return m
  }, [selectedMedia, orderBadges])
  const toggleLink = (id: number) => {
    if (!effectiveRemovable && excludedLinks.has(id)) return
    setSelectedLinks(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      notePick('link', id, next.has(id))
      return next
    })
    // Seed this link's display time the first time it's picked.
    setLinkDurations(prev => prev[id] != null ? prev : { ...prev, [id]: DEFAULT_DURATION })
  }
  const setLinkDuration = (id: number, seconds: number) =>
    setLinkDurations(prev => ({ ...prev, [id]: Math.max(1, Math.min(86400, Math.round(seconds) || 1)) }))
  // Playlists are SINGLE-select: a screen's content channel holds one
  // occupant, and a playlist is already a complete rotation. Two of them can't
  // both play — the old behaviour accepted the second pick and then published
  // only the first, dropping the rest silently. Picking another now REPLACES
  // the current one rather than dead-ending on a disabled tile, so the
  // operator is never stuck wondering why a click did nothing.
  const togglePlaylist = (id: number) => {
    if (!effectiveRemovable && excludedPlaylists.has(id)) return
    setSelectedPlaylists(prev => {
      if (prev.has(id)) {
        // Deselecting the current one.
        notePick('playlist', id, false)
        return new Set<number>()
      }
      // Selecting a different one: drop whatever was held first so the
      // ordered selection never carries two.
      prev.forEach(prevId => notePick('playlist', prevId, false))
      notePick('playlist', id, true)
      return new Set([id])
    })
  }

  // Clear the whole working selection — the "Remove all" action on the
  // Selected tab. In removable mode this also deselects already-assigned items,
  // so confirming clears the screen's content.
  const clearSelection = () => {
    setSelectedMedia(new Set())
    setSelectedLinks(new Set())
    setSelectedPlaylists(new Set())
    setPickOrder([])
  }

  // Builds the full working selection as PickedItems — shared by the modal's
  // confirm button and (in embedded mode) the live onSelectionChange effect.
  // Walks `pickOrder` — the operator's actual click sequence — rather than
  // concatenating the three kind-Sets. The old version returned
  // [...allMedia, ...allLinks, ...allPlaylists], which silently regrouped a
  // mixed selection by type before it ever reached the publish call.
  const buildPicked = (): PickedItem[] => {
    const mediaById    = new Map(items.map(a => [a.id, a]))
    const linkById     = new Map(links.map(l => [l.id, l]))
    const playlistById = new Map(playlists.map(p => [p.id, p]))

    const out: PickedItem[] = []
    for (const key of pickOrder) {
      const [kind, rawId] = key.split(':')
      const id = Number(rawId)
      if (kind === 'media') {
        if (!selectedMedia.has(id)) continue
        const a = mediaById.get(id)
        if (!a) continue
        out.push(({
          kind: audioOnly ? 'audio' : 'media',
          ...a,
          duration: durations[a.id] ?? Math.max(1, Math.round(a.duration ?? DEFAULT_DURATION)),
        }) as PickedItem)
      } else if (kind === 'link') {
        if (!selectedLinks.has(id)) continue
        const l = linkById.get(id)
        if (!l) continue
        out.push({
          kind: 'link', id: l.id, name: l.name, interactive_link: l.interactive_link,
          duration: linkDurations[l.id] ?? DEFAULT_DURATION,
        })
      } else if (kind === 'playlist') {
        if (!selectedPlaylists.has(id)) continue
        const p = playlistById.get(id)
        if (!p) continue
        out.push({
          kind: 'playlist',
          id: p.id,
          name: p.name,
          item_count: (p.media?.length ?? 0) + (p.links?.length ?? 0),
        })
      }
    }
    return out
  }

  const confirm = () => onConfirm(buildPicked())

  // Embedded mode has no confirm button — the host mirrors the working
  // selection live as the operator toggles items / edits durations.
  useEffect(() => {
    if (!embedded || !onSelectionChange) return
    onSelectionChange(buildPicked())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, selectedMedia, selectedLinks, selectedPlaylists, pickOrder, durations, linkDurations, items, links, playlists])

  const totalSelected = selectedMedia.size + selectedLinks.size + selectedPlaylists.size
  const docCount      = items.filter(a => a.file_type === 'application/pdf' || /\.pdf$/i.test(a.original_name)).length
  const linkCount     = links.length
  const playlistCount = playlists.length
  const currentCount  = excluded.size + excludedLinks.size + excludedPlaylists.size
  // Complete numbering — every tab carries a count, not just the ones that
  // used to (Documents/Webpages/Playlists/Selected/Current). All = every
  // pickable item across every kind; Media = images/videos only (documents
  // split out into their own tab).
  const mediaCount    = items.length - docCount
  const allCount      = items.length + linkCount + (includePlaylists ? playlistCount : 0)

  // Embedded mode never renders when the host has it "closed" (mirrors the
  // modal's own open-gating without paying for the Dialog wrapper).
  if (embedded && !open) return null

  const nativeBody = (
    <>
        {/* Tab strip — flush with the dialog top edge, same styling as the
            Asset page tabs so the modal reads as "the Asset page in a box". */}
        <div className="px-4 pt-4 border-b border-border">
          <div className="flex gap-1 overflow-x-auto no-scrollbar">
            {audioOnly ? (
              <TabButton active onClick={() => setTab('audio')} icon={Music} label={t('playlist.picker.tabs.audio', 'Audio')} />
            ) : (
              <>
                {tabs.includes('current')    && <TabButton active={tab === 'current'}    onClick={() => setTab('current')}    icon={MonitorPlay} label={t('playlist.picker.tabs.current', 'Currently Showing')} badge={currentCount || undefined} />}
                {tabs.includes('all')        && <TabButton active={tab === 'all'}        onClick={() => setTab('all')}        icon={Inbox}     label={t('assets.tabs.all',        'All')} badge={allCount || undefined} />}
                {tabs.includes('media')      && <TabButton active={tab === 'media'}      onClick={() => setTab('media')}      icon={ImageIcon} label={t('assets.tabs.media',      'Media')} badge={mediaCount || undefined} />}
                {tabs.includes('documents')  && <TabButton active={tab === 'documents'}  onClick={() => setTab('documents')}  icon={FileText}  label={t('assets.tabs.documents',  'Documents')} badge={docCount     || undefined} />}
                {tabs.includes('links')      && <TabButton active={tab === 'links'}      onClick={() => setTab('links')}      icon={Link2}     label={t('playlist.picker.tabs.links', 'Webpages')} badge={linkCount     || undefined} />}
                {tabs.includes('playlists')  && <TabButton active={tab === 'playlists'}  onClick={() => setTab('playlists')}  icon={ListMusic} label={t('playlist.picker.tabs.playlists', 'Playlists')} badge={playlistCount || undefined} />}
                {/* Selected — always available: review the working selection and
                    remove some or all before confirming. */}
                <TabButton active={tab === 'selected'} onClick={() => setTab('selected')} icon={CheckCircle2} label={t('playlist.picker.tabs.selected', 'Selected')} badge={totalSelected || undefined} />
              </>
            )}
          </div>
        </div>

        {/* Search + filter row — search left, type filter right. Search
            picks up the same red focus styling as the Asset page. */}
        <div className="px-4 pt-3 flex items-center gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder={t('common.search', 'Search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="ps-9 h-9 transition-colors focus-visible:border-primary focus-visible:ring-primary/30 focus-visible:text-foreground"
            />
          </div>
          {!audioOnly && tab !== 'documents' && tab !== 'links' && tab !== 'playlists' && tab !== 'current' && tab !== 'selected' && (
            <Select value={typeFilter} onValueChange={v => setTypeFilter(v as any)}>
              <SelectTrigger className="w-[130px] bg-background h-9"><SelectValue placeholder={t('common.type', 'Type')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('screens.allTypes', 'All Types')}</SelectItem>
                <SelectItem value="image">{t('screens.images', 'Images')}</SelectItem>
                <SelectItem value="gif">{t('screens.gifs', 'GIFs')}</SelectItem>
                <SelectItem value="video">{t('screens.videos', 'Videos')}</SelectItem>
              </SelectContent>
            </Select>
          )}
          {/* Quick-upload for media + documents. PDFs upload as plain documents;
              the "Document vs Folder of images" chooser stays on the Assets page
              for operators who want page-by-page signage. */}
          {tab !== 'links' && tab !== 'playlists' && tab !== 'current' && tab !== 'selected' && (
            <Button
              type="button"
              variant="outline"
              className="h-9 gap-2 shrink-0"
              onClick={openPickerUpload}
            >
              <Upload className="w-4 h-4" />
              {t('playlist.picker.upload', 'Upload')}
            </Button>
          )}
          {/* Remove all — clears the whole working selection from the Selected tab. */}
          {tab === 'selected' && totalSelected > 0 && (
            <Button
              type="button"
              variant="outline"
              className="h-9 gap-2 shrink-0 text-destructive hover:text-destructive"
              onClick={clearSelection}
            >
              <Trash2 className="w-4 h-4" />
              {t('playlist.picker.removeAll', 'Remove all')}
            </Button>
          )}
        </div>

        {/* Grid body */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-4">
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="aspect-video w-full rounded-xl" />)}
            </div>
          ) : (filteredMedia.length === 0 && filteredLinks.length === 0 && filteredPlaylists.length === 0) ? (
            <p className="text-sm text-muted-foreground text-center py-12">
              {tab === 'audio'     ? t('playlist.picker.noAudio',     'No audio yet. Upload a track to get started.')
              : tab === 'selected'  ? t('playlist.picker.noSelected',  'Nothing selected yet — pick content from the other tabs.')
              : tab === 'current'   ? (currentEmptyLabel ?? t('playlist.picker.noCurrent', 'Nothing assigned yet — switch to another tab to add content.'))
              : tab === 'links'     ? t('playlist.picker.noLinks',     'No webpages yet. Create one from the Asset → Webpages tab.')
              : tab === 'documents' ? t('playlist.picker.noDocuments', 'No documents yet. Upload a PDF to get started.')
              : tab === 'playlists' ? t('playlist.picker.noPlaylists', 'No playlists yet. Create one from the Collections → Playlists tab.')
              : t('common.noData', 'No data found')}
            </p>
          ) : (
            <div className="space-y-4">
              {filteredMedia.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {filteredMedia.map(a => (
                    <AssetThumb
                      key={`m-${a.id}`}
                      asset={a}
                      selected={selectedMedia.has(a.id)}
                      disabled={!effectiveRemovable && excluded.has(a.id)}
                      onToggle={() => toggleMedia(a.id)}
                      order={mediaOrder.get(a.id)}
                      duration={durations[a.id] ?? Math.max(1, Math.round(a.duration ?? DEFAULT_DURATION))}
                      onDuration={audioOnly ? undefined : (s) => setDuration(a.id, s)}
                    />
                  ))}
                </div>
              )}
              {filteredLinks.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {filteredLinks.map(l => (
                    <LinkThumb
                      key={`l-${l.id}`}
                      link={l}
                      selected={selectedLinks.has(l.id)}
                      disabled={!effectiveRemovable && excludedLinks.has(l.id)}
                      onToggle={() => toggleLink(l.id)}
                      order={orderBadges.get(`link:${l.id}`)}
                      duration={linkDurations[l.id] ?? DEFAULT_DURATION}
                      onDuration={(s) => setLinkDuration(l.id, s)}
                    />
                  ))}
                </div>
              )}
              {filteredPlaylists.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {filteredPlaylists.map(p => (
                    <PlaylistThumb
                      key={`p-${p.id}`}
                      playlist={p}
                      selected={selectedPlaylists.has(p.id)}
                      disabled={!effectiveRemovable && excludedPlaylists.has(p.id)}
                      onToggle={() => togglePlaylist(p.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer — cancel / confirm. Embedded mode skips this entirely; the
            host's own dialog footer owns Cancel/Save and the selection is
            already live via onSelectionChange. */}
        {!embedded && (
          <div className="px-4 py-3 border-t border-border flex items-center justify-between gap-3">
            <p className="text-xs text-muted-foreground">
              {totalSelected > 0
                ? t('playlist.picker.selectedN', '{{count}} selected', { count: totalSelected })
                : t('playlist.picker.pickPrompt', 'Pick the items you want to add')}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
              <Button disabled={!removable && totalSelected === 0} onClick={confirm} className="gap-2">
                <CheckCircle2 className="w-4 h-4" />
                {confirmLabel
                  ?? (removable
                    ? t('playlist.picker.save', 'Save changes')
                    : t('playlist.picker.add', 'Add to playlist'))}
              </Button>
            </div>
          </div>
        )}
    </>
  )

  // ── Custom mode body ─────────────────────────────────────────────────────
  // Search + a grouped grid of caller-supplied tiles, single-select (tap
  // picks and — in modal mode — closes immediately). No tab strip (there's
  // only ever one "kind" here) and no Confirm footer.
  const customBody = (
    <>
      <div className="px-4 pt-4 flex items-center gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={searchPlaceholder ?? t('common.search', 'Search')}
            className="ps-9 h-9 transition-colors focus-visible:border-primary focus-visible:ring-primary/30 focus-visible:text-foreground"
            autoFocus
          />
        </div>
        {customSelected != null && (
          <button
            type="button"
            onClick={() => pickCustom(customSelected)}
            className="h-9 px-3 rounded-md border border-border text-sm text-muted-foreground hover:text-destructive hover:border-destructive/40 transition-colors shrink-0"
          >
            {t('companions.none', 'None')}
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden border-t border-border mt-3 p-4">
        {filteredCustomGroups.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-12">{t('common.noData', 'No data found')}</p>
        ) : (
          <div className="space-y-4">
            {filteredCustomGroups.map(g => (
              <div key={g.key}>
                {(customGroups?.length ?? 0) > 1 && (
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">{g.label}</p>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {g.items.map(item => (
                    <CustomThumb
                      key={`${g.key}-${item.id}`}
                      item={item}
                      selected={customSelected === `${g.key}:${item.id}`}
                      onToggle={() => pickCustom(`${g.key}:${item.id}`)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )

  const body = isCustom ? customBody : nativeBody

  if (embedded) {
    return <div className="flex flex-col h-full min-h-0">{body}</div>
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="max-w-4xl p-0 overflow-hidden flex flex-col max-h-[85vh]">
        {isCustom && title && (
          <DialogHeader className="px-5 pt-5 pb-0 shrink-0">
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
        )}
        {body}
      </DialogContent>
    </Dialog>
  )
}

// Custom-mode tile — thumbnail when the item has one, an icon tile
// otherwise (audio / stop screens without a renderable frame). Single-select:
// no "Already in" disabled state, no order badge, no duration control — this
// is a plain "pick one" tile, ported from the old MixContentPicker.
function CustomThumb({
  item, selected, onToggle,
}: {
  item: PickerCardItem
  selected: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  const Icon = item.icon
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`group relative text-start rounded-xl overflow-hidden border transition-all ${
        selected ? 'border-primary ring-2 ring-primary/30 bg-muted' : 'border-border/60 bg-card hover:border-primary/40'
      }`}
    >
      <div className={`w-full aspect-video overflow-hidden flex items-center justify-center ${item.thumb ? 'bg-muted' : item.colorClass}`}>
        {item.thumb
          ? <img src={item.thumb} alt={item.label} className="w-full h-full object-cover" loading="lazy" />
          : <Icon className="w-9 h-9" />}
        {selected && (
          <div className="absolute top-2 end-2">
            <Badge variant="secondary" className="gap-1 text-[10px] py-0 h-5 bg-primary text-primary-foreground">
              <CheckCircle2 className="w-2.5 h-2.5" />
              {t('common.selected', 'Selected')}
            </Badge>
          </div>
        )}
      </div>
      <div className="px-3 py-2">
        <p className="text-xs font-medium truncate leading-snug">{item.label}</p>
      </div>
    </button>
  )
}

function AssetThumb({
  asset, selected, disabled, onToggle, order, duration, onDuration,
}: {
  asset: AssetItem
  selected: boolean
  disabled: boolean
  onToggle: () => void
  /** 1-based display order, shown as a badge while the item is selected. */
  order?: number
  /** Per-item display time in seconds (editable when onDuration is set). */
  duration?: number
  /** Called with the new time (seconds). Omitted → the time input is hidden. */
  onDuration?: (seconds: number) => void
}) {
  const { t } = useTranslation()
  const url = asset.url.startsWith('http') ? asset.url : `${BACKEND_URL}${asset.url}`
  const isImage = asset.file_type.startsWith('image')
  const isVideo = asset.file_type.startsWith('video')
  const isAudio = asset.file_type.startsWith('audio')
  const isPdf   = asset.file_type === 'application/pdf' || /\.pdf$/i.test(asset.original_name)
  // The card is a div (not a button) so the time <input> below is a valid,
  // independently-focusable control rather than a nested interactive element.
  return (
    <div
      className={`group relative rounded-xl overflow-hidden border transition-all ${
        disabled
          ? 'border-border/50 opacity-50'
          : selected
            ? 'border-primary ring-2 ring-primary/30 bg-muted'
            : 'border-border/60 bg-card hover:border-primary/40'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className={`block w-full text-start ${disabled ? 'cursor-not-allowed' : ''}`}
      >
        <div className="block w-full aspect-video bg-muted overflow-hidden">
          {isImage && <img src={url} alt={asset.original_name} className="w-full h-full object-cover" loading="lazy" />}
          {isVideo && (
            // Native first-frame poster via #t=0.1 so videos show a real
            // thumbnail in the picker, not just an icon.
            <video src={`${url}#t=0.1`} className="w-full h-full object-cover" muted playsInline preload="metadata" />
          )}
          {isPdf && (
            <PdfThumbnail url={url} targetWidth={480} fit="cover" className="w-full h-full" />
          )}
          {isAudio && (
            <div className="w-full h-full flex items-center justify-center bg-indigo-500/10">
              <Music className="w-10 h-10 text-indigo-500" />
            </div>
          )}
          {/* Display-order number — appears once the item is selected. */}
          {selected && order != null && (
            <div className="absolute top-2 start-2">
              <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-bold text-primary-foreground shadow ring-2 ring-background tabular-nums">
                {order}
              </span>
            </div>
          )}
          {(selected || disabled) && (
            <div className="absolute top-2 end-2">
              <Badge variant="secondary" className={`gap-1 text-[10px] py-0 h-5 ${selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                <CheckCircle2 className="w-2.5 h-2.5" />
                {disabled ? t('playlist.picker.alreadyIn', 'Already in') : t('common.selected', 'Selected')}
              </Badge>
            </div>
          )}
        </div>
        <div className="px-3 py-2">
          <p className="text-xs font-medium truncate leading-snug">{asset.original_name}</p>
        </div>
      </button>

      {/* Per-item display time — shown for selected items so the operator sets
          how long each one stays on screen. Sits outside the toggle button so
          typing doesn't flip the selection. */}
      {selected && !disabled && onDuration && (
        <div className="flex items-center gap-1.5 px-3 pb-2 -mt-0.5">
          <Clock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <input
            type="number"
            min={1}
            value={duration ?? DEFAULT_DURATION}
            onChange={e => onDuration(Number(e.target.value))}
            aria-label={t('playlist.picker.displayTime', 'Display time (seconds)')}
            className="w-14 h-7 rounded-md border border-border bg-background px-1.5 text-xs text-center tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0"
          />
          <span className="text-[11px] text-muted-foreground">{t('playlist.picker.seconds', 'sec')}</span>
        </div>
      )}
    </div>
  )
}

function LinkThumb({
  link, selected, disabled, onToggle, order, duration, onDuration,
}: {
  link: LinkItem
  selected: boolean
  disabled: boolean
  onToggle: () => void
  /** 1-based display order, shown as a badge while the item is selected —
   *  numbered across the whole selection, not just the links, since links and
   *  media share one rotation. */
  order?: number
  /** Per-item display time in seconds (editable when onDuration is set). */
  duration?: number
  /** Called with the new time (seconds). Omitted → the time input is hidden. */
  onDuration?: (seconds: number) => void
}) {
  const { t } = useTranslation()
  // A div (not a button) so the time <input> below is a valid, independently
  // -focusable control instead of a nested interactive element — same shape
  // as AssetThumb.
  return (
    <div
      className={`group relative rounded-xl overflow-hidden border transition-all ${
        disabled
          ? 'border-border/50 opacity-50'
          : selected
            ? 'border-primary ring-2 ring-primary/30 bg-muted'
            : 'border-border/60 bg-card hover:border-primary/40'
      }`}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        className={`block w-full text-start ${disabled ? 'cursor-not-allowed' : ''}`}
      >
        <div className="block w-full aspect-video bg-teal-500/10 overflow-hidden flex items-center justify-center">
          <Link2 className="w-10 h-10 text-teal-600" />
          {/* Display-order number — same treatment as AssetThumb. */}
          {selected && order != null && (
            <div className="absolute top-2 start-2">
              <span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-bold text-primary-foreground shadow ring-2 ring-background tabular-nums">
                {order}
              </span>
            </div>
          )}
          {(selected || disabled) && (
            <div className="absolute top-2 end-2">
              <Badge variant="secondary" className={`gap-1 text-[10px] py-0 h-5 ${selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                <CheckCircle2 className="w-2.5 h-2.5" />
                {disabled ? t('playlist.picker.alreadyIn', 'Already in') : t('common.selected', 'Selected')}
              </Badge>
            </div>
          )}
        </div>
        <div className="px-3 py-2">
          <p className="text-xs font-medium truncate leading-snug">{link.name}</p>
          <p className="text-[10px] text-muted-foreground truncate">{link.interactive_link}</p>
        </div>
      </button>

      {/* Per-item display time — same control as media tiles, since a picked
          link has no natural duration of its own. */}
      {selected && !disabled && onDuration && (
        <div className="flex items-center gap-1.5 px-3 pb-2 -mt-0.5">
          <Clock className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          <input
            type="number"
            min={1}
            value={duration ?? DEFAULT_DURATION}
            onChange={e => onDuration(Number(e.target.value))}
            aria-label={t('playlist.picker.displayTime', 'Display time (seconds)')}
            className="w-14 h-7 rounded-md border border-border bg-background px-1.5 text-xs text-center tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0"
          />
          <span className="text-[11px] text-muted-foreground">{t('playlist.picker.seconds', 'sec')}</span>
        </div>
      )}
    </div>
  )
}

function PlaylistThumb({
  playlist, selected, disabled, onToggle,
}: {
  playlist: PlaylistItem
  selected: boolean
  disabled: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  const count = (playlist.media?.length ?? 0) + (playlist.links?.length ?? 0)
  // Reuse the shared helper so this tile shows the same thumbnail (first
  // renderable image, or a cached video frame) as the Content Hub card.
  const thumb = playlistThumbUrl(
    playlist.media?.filter((m): m is { id: number; file_type: string; url: string } =>
      typeof m.file_type === 'string' && typeof m.url === 'string'),
  )
  return (
    <button
      type="button"
      onClick={onToggle}
      disabled={disabled}
      className={`group relative text-start rounded-xl overflow-hidden border transition-all ${
        disabled
          ? 'border-border/50 opacity-50 cursor-not-allowed'
          : selected
            ? 'border-primary ring-2 ring-primary/30 bg-muted'
            : 'border-border/60 bg-card hover:border-primary/40'
      }`}
    >
      <div className="block w-full aspect-video bg-amber-500/10 overflow-hidden relative">
        {thumb ? (
          <img src={thumb} alt={playlist.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ListMusic className="w-10 h-10 text-amber-600" />
          </div>
        )}
        {(selected || disabled) && (
          <div className="absolute top-2 end-2">
            <Badge variant="secondary" className={`gap-1 text-[10px] py-0 h-5 ${selected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
              <CheckCircle2 className="w-2.5 h-2.5" />
              {disabled ? t('playlist.picker.alreadyIn', 'Already in') : t('common.selected', 'Selected')}
            </Badge>
          </div>
        )}
      </div>
      <div className="px-3 py-2">
        <p className="text-xs font-medium truncate leading-snug">{playlist.name}</p>
        <p className="text-[10px] text-muted-foreground">
          {t('playlist.picker.itemCount', { defaultValue: '{{count}} items', count })}
        </p>
      </div>
    </button>
  )
}

